import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { FiArrowLeft, FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import { getCommonProductById, getCommonProductSalesStats } from "../../services/commonProductService";
import { getPlatformProducts } from "../../services/platformProductService";
import { getSubCategories } from "../../services/subCategoryService";

const CommonProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const { data: subCategoriesData } = useQuery(
    ["subCategories"],
    () => getSubCategories({ page: 1, limit: 1000 }),
    { refetchOnWindowFocus: false }
  );
  const subCategories = subCategoriesData?.data?.data || [];
  const getSubCategoryName = (subCatId) => {
    const subCat = subCategories.find((sc) => sc.id === subCatId);
    return subCat?.name || "Uncategorized";
  };

  const { data: commonProductDetail, refetch: refetchDetail, isLoading: detailLoading } = useQuery(
    ["commonProductDetailPage", id],
    () => getCommonProductById(id),
    {
      enabled: !!id,
      onError: (error) => toast.error(error.message || "Failed to load product detail"),
      refetchOnWindowFocus: false,
    }
  );

  const baseSku = commonProductDetail?.data?.baseSku || "";
  const { data: platformProductsData, refetch: refetchPlatforms, isLoading: platformsLoading } = useQuery(
    ["platformProductsForCommon", id, page, limit, baseSku],
    () =>
      getPlatformProducts({
        page,
        limit,
        searchKey: baseSku || commonProductDetail?.data?.name || "",
        productId: id,
      }),
    {
      enabled: !!id,
      onError: (error) => toast.error(error.message || "Failed to load platform products"),
      refetchOnWindowFocus: false,
    }
  );

  const platformProducts = platformProductsData?.data?.data || [];
  const totalCount = platformProductsData?.data?.count || 0;
  const totalPages = Math.ceil(totalCount / limit) || 1;

  const { data: salesStatsData, refetch: refetchSalesStats } = useQuery(
    ["commonProductSalesStats", id],
    () => getCommonProductSalesStats(id),
    { enabled: !!id, refetchOnWindowFocus: false }
  );
  const unitsSold = salesStatsData?.data?.unitsSold ?? null;

  const handleRefresh = () => {
    refetchDetail();
    refetchPlatforms();
    refetchSalesStats();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <button
              onClick={() => {
                if (location.state?.fromPlatformProduct) {
                  navigate("/products", { state: { ...location.state, tab: "platformProducts" } });
                } else if (location.state?.fromProducts) {
                  navigate("/products", { state: location.state });
                } else {
                  navigate(-1);
                }
              }}
              className="flex items-center text-blue-600 hover:text-blue-700"
            >
              <FiArrowLeft className="mr-1" /> Back
            </button>
           
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Common Product Detail</h1>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <FiRefreshCw size={16} className="mr-2" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {detailLoading ? (
            <p className="text-sm text-gray-500">Loading product details...</p>
          ) : !commonProductDetail?.data ? (
            <p className="text-sm text-gray-500">Product not found.</p>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{commonProductDetail.data.name}</h3>
                  <p className="text-sm text-gray-500">SKU: {commonProductDetail.data.baseSku || "-"}</p>
                  <p className="text-sm text-gray-500">Brand: {commonProductDetail.data.brand || "-"}</p>
                  <p className="text-sm text-gray-500">
                    Sub-Category: {getSubCategoryName(commonProductDetail.data.subCategoryId)}
                  </p>
                  {unitsSold !== null && (
                    <p className="text-sm text-gray-500">
                      Units sold: <span className="font-medium text-gray-700">{unitsSold}</span>
                    </p>
                  )}
                  <p className="text-sm text-gray-500">
                    Status: {commonProductDetail.data.isActive ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
              {commonProductDetail.data.description && (
                <p className="mt-3 text-sm text-gray-700">{commonProductDetail.data.description}</p>
              )}
            </>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h4 className="text-base font-semibold text-gray-900">Platform products with same SKU</h4>
            <p className="text-sm text-gray-500">Linked platform products or those matching the SKU.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {platformsLoading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-6 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : platformProducts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-6 text-center text-gray-500">No platform products found</td>
                  </tr>
                ) : (
                  platformProducts.map((pp) => (
                    <tr key={pp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pp.postingAccount}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{pp.productName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pp.sku || "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pp.channelSku || "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {pp.productId ? "Linked" : "Not linked"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalCount > limit && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
                    <span className="font-medium">{Math.min(page * limit, totalCount)}</span> of{" "}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 10) {
                        pageNum = i + 1;
                      } else if (page <= 5) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 4) {
                        pageNum = totalPages - 9 + i;
                      } else {
                        pageNum = page - 5 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === page
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommonProductDetail;

